import React, { useEffect } from 'react';

const LandingPage = ({ onCompanyLogin, onMemberLogin }) => {
  useEffect(() => {
    // Header scroll effect
    const handleScroll = () => {
      const header = document.getElementById('header');
      if (window.scrollY > 50) {
        header?.classList.add('scrolled');
      } else {
        header?.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Smooth scrolling for anchor links
    const handleAnchorClick = (e) => {
      if (e.target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const target = document.querySelector(e.target.getAttribute('href'));
        if (target) {
          const headerHeight = document.querySelector('.header')?.offsetHeight || 80;
          const targetPosition = target.offsetTop - headerHeight;
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);

    // Intersection Observer for animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-on-scroll');
        }
      });
    }, observerOptions);

    // Observe all cards and sections
    const elementsToObserve = document.querySelectorAll('.feature-card, .step-card, .benefit-item, .audience-card');
    elementsToObserve.forEach(el => observer.observe(el));

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleAnchorClick);
      observer.disconnect();
    };
  }, []);

  const handleCompanyLogin = () => {
    if (onCompanyLogin) {
      onCompanyLogin();
    } else {
      window.location.href = 'https://payments-app-silk.vercel.app/company-login';
    }
  };

  const handleMemberLogin = () => {
    if (onMemberLogin) {
      onMemberLogin();
    } else {
      window.location.href = 'https://payments-app-silk.vercel.app/member-login';
    }
  };

  return (
    <>
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
          color: #1a1a1a;
          scroll-behavior: smooth;
        }

        /* Header */
        .header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(240, 240, 240, 0.8);
          z-index: 1000;
          transition: all 0.3s ease;
        }

        .header.scrolled {
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
        }

        .nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 80px;
        }

        .logo-section {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .logo {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background: linear-gradient(135deg, #d4af37, #b8941f);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 18px;
        }

        .company-name {
          font-size: 18px;
          font-weight: 500;
          color: #1a1a1a;
        }

        .nav-buttons {
          display: flex;
          gap: 12px;
        }

        .nav-btn {
          padding: 12px 24px;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          text-decoration: none;
          color: #666;
          font-weight: 500;
          font-size: 14px;
          transition: all 0.3s ease;
          background: white;
          cursor: pointer;
        }

        .nav-btn:hover {
          border-color: #d4af37;
          color: #d4af37;
          transform: translateY(-1px);
        }

        .nav-btn.primary {
          background: linear-gradient(135deg, #d4af37, #b8941f);
          color: white;
          border-color: transparent;
        }

        .nav-btn.primary:hover {
          background: linear-gradient(135deg, #b8941f, #a0811b);
          color: white;
        }

        /* Hero Section */
        .hero {
          padding: 120px 24px 80px;
          background: linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(248,248,248,0.5) 100%);
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          opacity: 0.03;
          background-image:
            radial-gradient(circle at 25px 25px, #d4af37 2px, transparent 0),
            radial-gradient(circle at 75px 75px, #d4af37 1px, transparent 0);
          background-size: 100px 100px;
        }

        .hero-container {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }

        .hero-content {
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
        }

        .hero-title {
          font-family: 'Georgia', serif;
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 400;
          color: #1a1a1a;
          margin-bottom: 24px;
          letter-spacing: -0.02em;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.25rem;
          color: #666;
          margin-bottom: 40px;
          font-weight: 300;
        }

        .hero-highlight {
          background: linear-gradient(135deg, #d4af37, #b8941f);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .cta-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 60px;
        }

        .cta-btn {
          padding: 16px 32px;
          border-radius: 12px;
          text-decoration: none;
          font-weight: 500;
          font-size: 16px;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          cursor: pointer;
          border: none;
        }

        .cta-btn.primary {
          background: linear-gradient(135deg, #d4af37, #b8941f);
          color: white;
        }

        .cta-btn.secondary {
          background: white;
          color: #666;
          border: 1px solid #e0e0e0;
        }

        .cta-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(212, 175, 55, 0.25);
        }

        /* Features Grid */
        .features {
          padding: 80px 24px;
          background: white;
        }

        .features-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-title {
          font-family: 'Georgia', serif;
          font-size: 2.5rem;
          font-weight: 400;
          color: #1a1a1a;
          margin-bottom: 16px;
        }

        .section-subtitle {
          font-size: 1.1rem;
          color: #666;
          max-width: 600px;
          margin: 0 auto;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 32px;
        }

        .feature-card {
          background: white;
          border: 1px solid #f0f0f0;
          border-radius: 16px;
          padding: 32px;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .feature-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #d4af37, #b8941f);
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }

        .feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
          border-color: rgba(212, 175, 55, 0.2);
        }

        .feature-card:hover::before {
          transform: scaleX(1);
        }

        .feature-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #d4af37, #b8941f);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          color: white;
          font-size: 24px;
        }

        .feature-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 12px;
        }

        .feature-description {
          color: #666;
          line-height: 1.6;
        }

        /* How It Works */
        .how-it-works {
          padding: 80px 24px;
          background: #fafafa;
        }

        .steps-container {
          max-width: 1000px;
          margin: 0 auto;
        }

        .steps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 40px;
        }

        .step-card {
          text-align: center;
          position: relative;
        }

        .step-number {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #d4af37, #b8941f);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          color: white;
          font-weight: 600;
          font-size: 1.5rem;
        }

        .step-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 12px;
        }

        .step-description {
          color: #666;
        }

        /* Benefits */
        .benefits {
          padding: 80px 24px;
          background: white;
        }

        .benefits-grid {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 32px;
        }

        .benefit-item {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          padding: 24px;
          border-radius: 12px;
          transition: all 0.3s ease;
        }

        .benefit-item:hover {
          background: #fafafa;
          transform: translateX(8px);
        }

        .benefit-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #d4af37, #b8941f);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 20px;
          flex-shrink: 0;
        }

        .benefit-content h4 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 8px;
        }

        .benefit-content p {
          color: #666;
          font-size: 0.95rem;
        }

        /* Target Audience */
        .target-audience {
          padding: 80px 24px;
          background: linear-gradient(135deg, #f8f8f8, #ffffff);
        }

        .audience-grid {
          max-width: 1000px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 32px;
        }

        .audience-card {
          background: white;
          border-radius: 16px;
          padding: 32px;
          text-align: center;
          border: 1px solid #f0f0f0;
          transition: all 0.3s ease;
        }

        .audience-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
        }

        .audience-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #d4af37, #b8941f);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          color: white;
          font-size: 32px;
        }

        .audience-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 12px;
        }

        .audience-description {
          color: #666;
        }

        .contact-item {
        align-items: flex-start; /* better for multi-line */
        }

        .phone-list {
        margin-top: 2px;
        }
        
        /* Footer */
        .footer {
          background: #1a1a1a;
          color: white;
          padding: 60px 24px 30px;
        }

        .footer-container {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 40px;
        }

        .footer-section h3 {
          color: #d4af37;
          font-size: 1.25rem;
          margin-bottom: 20px;
          font-weight: 600;
        }

        .footer-section p {
          color: #cccccc;
          line-height: 1.6;
          margin-bottom: 12px;
        }

        .footer-link {
        color: white;
        text-decoration: none;
        margin: 0 5px;
        }

        .footer-link:hover {
        text-decoration: underline;
        }  

        .contact-item {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }

        .contact-icon {
          width: 32px;
          height: 32px;
          background: #d4af37;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 14px;
        }

        .phone-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .footer-bottom {
          border-top: 1px solid #333;
          margin-top: 40px;
          padding-top: 20px;
          text-align: center;
          color: white;
        }

        /* Mobile Responsiveness */
        @media (max-width: 768px) {
          .nav-container {
            padding: 0 16px;
            height: 70px;
          }

          .nav-buttons {
            gap: 8px;
          }

          .nav-btn {
            padding: 10px 16px;
            font-size: 13px;
          }

          .hero {
            padding: 100px 16px 60px;
          }

          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }

          .cta-btn {
            width: 100%;
            max-width: 280px;
          }

          .features,
          .how-it-works,
          .benefits,
          .target-audience {
            padding: 60px 16px;
          }

          .features-grid,
          .steps-grid,
          .benefits-grid,
          .audience-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Scroll animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-on-scroll {
          animation: fadeInUp 0.6s ease-out;
        }
      `}</style>

      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=Georgia:wght@400;700&display=swap" rel="stylesheet" />
      <link href='https://unpkg.com/boxicons@2.1.4/css/boxicons.min.css' rel='stylesheet' />

      {/* Header */}
      <header className="header" id="header">
        <nav className="nav-container">
          <div className="logo-section">
            <div className="logo">A</div>
            <span className="company-name">Aashritha E Solution</span>
          </div>
          <div className="nav-buttons">
            <button onClick={handleCompanyLogin} className="nav-btn">Company Login</button>
            <button onClick={handleMemberLogin} className="nav-btn primary">Member Login</button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="hero-highlight">Aashritha E Solution</span><br />
            </h1>
            <h2>We provide best in class services to Jewellers and Temples.</h2>
            <div className="cta-buttons">
              <a href="#target-audience" className="cta-btn secondary">For Demo</a>
              <a href="#how-it-works" className="cta-btn secondary">Learn More</a>
            </div>
          </div>
        </div>
      </section>

     {/* Features Section */}
<section className="features">
  <div className="features-container">
    <div className="section-header"></div>

    <div className="features-grid">
      <div className="feature-card">
        <div className="feature-icon">
          <i className="bx bxl-dribbble"></i>
        </div>
        <h3 className="feature-title">Experience</h3>
        <p className="feature-description">
          Starting in 1999, we have the history of delivering quality services in jewellery
          field for over 22 years now.
        </p>
      </div>

      <div className="feature-card">
        <div className="feature-icon">
          <i className="bx bxl-dribbble"></i>
        </div>
        <h3 className="feature-title">Customization</h3>
        <p className="feature-description">
          Flexible customization and add on features of our products will help you get the
          right solution for your business.
        </p>
      </div>

      <div className="feature-card">
        <div className="feature-icon">
          <i className="bx bxl-dribbble"></i>
        </div>
        <h3 className="feature-title">Quality and User friendly</h3>
        <p className="feature-description">
          Best in class user friendly software keeping quality in mind will deliver excellent
          performance.
        </p>
      </div>

      <div className="feature-card">
        <div className="feature-icon">
          <i className="bx bxl-dribbble"></i>
        </div>
        <h3 className="feature-title">Maintenance and Service</h3>
        <p className="feature-description">
          Providing software solution that fulfill customer requirements and continuous
          training & maintenance.
        </p>
      </div>
    </div>
  </div>
</section>

      {/* How It Works */}
      <section className="how-it-works" id="how-it-works">
        <div className="steps-container">
          <div className="section-header">
            <h2 className="section-title">Check our Services</h2>
            <p className="section-subtitle">Our various softwares are listed below</p>
          </div>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3 className="step-title">916Advanced</h3>
              <p className="step-description">Jewellery management software for maintainig Stock,Accounts and Bills.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3 className="step-title">Scheme Software</h3>
              <p className="step-description">Jewellery schemes management software with variable groups and deposites, receipts.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3 className="step-title">Temple Software</h3>
              <p className="step-description">Temple management software for maintainig receipts and poojas.</p>
            </div>
            <div className="step-card">
              <div className="step-number">4</div>
              <h3 className="step-title">Pawn Ticket</h3>
              <p className="step-description">Fully equipped gold loan management software.</p>
            </div>
              <div className="step-card">
              <div className="step-number">5</div>
              <h3 className="step-title">Website Development</h3>
              <p className="step-description">Design beautiful websites to showcase your business.</p>
            </div>
             <div className="step-card">
              <div className="step-number">6</div>
              <h3 className="step-title">Mobile Applications</h3>
              <p className="step-description">Add on mobile device applications to 916 and schemes using android,smartphones and tablet.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits">
        <div className="section-header">
          <h2 className="section-title">Find Out More About Us</h2>
          <p className="section-subtitle">We are a software company situated in Udupi-Karnataka.</p>
         <p className="section-subtitle">We have over 450 Clients across India, majority in Karnataka.</p>
        <p className="section-subtitle">Started Software journey in 1999 and have taken up different kind of requirements from clients and proven excellence in delivering them.</p>
        <p className="section-subtitle">The software is developed by the team who has the clear understanding of the business process. We interact with the customer and understand their requirement. Making the business process easy and reducing the workaround time is at our utmost importance..</p>
        </div>
      </section>

      {/* Target Audience */}
      <section className="target-audience" id="target-audience">
        <div className="section-header">
          <h2 className="section-title">Contact Us</h2>
          <p className="section-subtitle">Any queries regarding our services, feel free to contact us!
          We are always available for help</p> 
          <p> Call and book a free demo now !</p>
        </div>
        <div className="audience-grid">
          <div className="audience-card">
            <div className="audience-icon">
          <i className='bx bx-mobile-alt'></i>
            </div>
            <h3 className="audience-title">Phone No</h3>
            <p className="audience-description">+91-9480733756</p>
            <p className="audience-description">+91-8496028756</p>
            <p className="audience-description">+91-8095222756</p>
            <p className="audience-description">+91-8496038756</p>
          </div>
          <div className="audience-card">
            <div className="audience-icon">
           <i className='bx bxs-envelope'></i>
            </div>
            <h3 className="audience-title">Gmail</h3>
            <p className="audience-description">aashritha756@yahoo.com</p>
          </div>
          <div className="audience-card">
            <div className="audience-icon">
            <i className='bx bx-map'></i>
            </div>
            <h3 className="audience-title">Address</h3>
            <p className="audience-description">Ground Floor Surabhi Lotus Building</p>
            <p className="audience-description">Ambalpady Highway Junction Udupi, Karnataka 576103</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-section">
            <h3>Aashritha E Solution</h3>
            <p>"Surabhi Lotus", Ground Floor<br />
            Brahmagiri, Ambalpady Highway Junction,<br />
            Ambalpady, Udupi-576103</p>
            <p style={{ marginTop: '1rem', color: '#d4af37' }}>Customer Satisifaction is Our Motto.</p>
          </div>
          <div className="footer-section">
            <h3>Contact Information</h3>
            <div className="contact-item">
              <div className="contact-icon">
                <i className='bx bx-envelope'></i>
              </div>
              <p>aashritha756@yahoo.com</p>
            </div>
            <div className="contact-item">
              <div className="contact-icon">
                <i className='bx bx-phone'></i>
              </div>
              <p>+91-9448128756</p>
            </div>
            <div className="contact-item">
              <div className="contact-icon">
                <i className='bx bx-building'></i>
              </div>
              <div className="phone-list">
                <p>+91-9480733756</p>
                <p>+91-8496028756</p>
                <p>+91-8095222756</p>
                <p>+91-8496038756</p>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 Aashritha E Solution. All rights reserved.</p>
            <a href="/terms" className="footer-link" target="_blank" rel="noopener noreferrer" >Terms & Conditions</a> |
            <a href="/privacy" className="footer-link" target="_blank" rel="noopener noreferrer">Privacy Policy</a> |
            <a href="/refund" className="footer-link" target="_blank" rel="noopener noreferrer">Refund Policy</a> |
            <a href="/contact" className="footer-link" target="_blank" rel="noopener noreferrer">Contact Us</a>
        </div>
      </footer>
    </>
  );
};

export default LandingPage;
